import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogIn } from "lucide-react";
import { toast } from "sonner";

export const AdminLoginPage = () => {
  const [isLogin, setIsLogin] = useState(true); // Registration disabled - login only
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/admin");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Login successful!");
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success("Registration successful!");
      }
      navigate("/admin");
    } catch (error) {
      console.error("Auth error:", error);

      // Extract error message - handle multiple error response formats
      let errorMessage = "Authentication failed";

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.statusText) {
        errorMessage = error.response.statusText;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show specific error for common cases
      if (error.response?.status === 429) {
        toast.error(`Account locked: ${errorMessage}`);
      } else if (error.response?.status === 401) {
        toast.error("Invalid email or password");
      } else if (error.response?.status === 403) {
        toast.error("Account is deactivated");
      } else if (!error.response) {
        toast.error("Cannot connect to server. Please try again in a few seconds.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      data-testid="admin-login-page"
    >
      <div className="max-w-md w-full">
        <div className="bg-white border-2 border-gray-900 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-red mb-4">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-playfair font-bold text-brand-green">
              {isLogin ? "Admin Login" : "Admin Registration"}
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="admin-auth-form"
          >
            {!isLogin && (
              <div>
                <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 border-2 border-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-green bg-gray-50"
                  data-testid="admin-name-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="w-full px-4 py-3 border-2 border-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-green bg-gray-50"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-inter font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
                className="w-full px-4 py-3 border-2 border-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-green bg-gray-50"
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-red text-white px-6 py-3 font-bold hover:bg-[#D62828] transition-colors uppercase tracking-wide disabled:opacity-50"
              data-testid="admin-submit-button"
            >
              {loading ? "Please wait..." : isLogin ? "Login" : "Register"}
            </button>
          </form>

          {/* Registration disabled - Only login allowed */}
        </div>
      </div>
    </div>
  );
};
